'use client'

import { useState, useMemo } from 'react'
import { History } from 'lucide-react'
import { HsCode, HsTreeResponseDto } from '@/modules/hs/types/hs-response'
import HsTreeToggle from './component/HsTreeToggle'
import HsNoteLinks from './component/HsNoteLinks'
import HsVersionHistoryModal from './component/HsVersionHistoryModal'
import BookmarkButton from '@/modules/hs/components/common/BookmarkButton'
import { removeHsCodeDashes } from '@/lib/utils'
import trungHsCodes from '@/modules/hs/constants/trung.json'

interface HsTaxDescriptionProps {
  hsCode: string
  hs: HsCode
  tree: HsTreeResponseDto
}


export default function HsDescription({
  hsCode,
  hs,
  tree,
}: HsTaxDescriptionProps) {
  const chapter = tree.chapters[0]
  const [versionModalOpen, setVersionModalOpen] = useState(false)

  // Check if this HS code has version history
  const hasVersionHistory = useMemo(() => {
    const cleanHsCode = hsCode.replace(/-/g, '')
    return trungHsCodes.includes(cleanHsCode)
  }, [hsCode])

  return (
    <section id="overview" className="mb-4 cl-card-border">
      {/* Main Overview Section */}
      <div className="case-card-border">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="mb-1.5 text-[28px] font-extrabold leading-tight">
              Mã HS {hsCode}
            </h1>
            <p className="text-base ">{removeHsCodeDashes(hs.descriptionVN)}</p>
          </div>
          <BookmarkButton hsId={hsCode} context="hs_detail" />
        </div>

        {/* Metadata Bar */}
        <div className="mb-5 flex flex-wrap gap-7 border-b border-[#e5e5e5] py-3.5">
          <MetadataItem icon="eye" label="lượt xem" value="1,245" />
          <MetadataItem icon="star" label="lượt lưu" value="89" />
          <div className="flex-1">
            Cơ sở pháp lý: Danh mục hàng hóa nhập khẩu Việt Nam cập nhật năm 2026
          </div>
          {hasVersionHistory && (
            <button
              onClick={() => setVersionModalOpen(true)}
              className="cursor-pointer inline-flex items-center gap-2 px-3.5 py-2 bg-white border-1.5 border-neutral-200 rounded-md text-[13px] font-semibold text-neutral-700 hover:border-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
            >
              <History className="size-4 text-neutral-500 hover:text-blue-600" />
              <span>Lịch sử phiên bản (2)</span>
            </button>
          )}
        </div>

        {/* Tree Toggle */}
        <HsTreeToggle tree={tree} hsCode={hsCode} />

        {/* Notes Links */}
        <HsNoteLinks
          partNote={tree.descriptionVN}
          partNumber={tree.numberVN}
          chapterNote={chapter?.descriptionVN}
          chapterNumber={chapter?.numberVN}
        />
      </div>

      {/* Version History Modal */}
      {hasVersionHistory && (
        <HsVersionHistoryModal
          open={versionModalOpen}
          onOpenChange={setVersionModalOpen}
          hsCode={hsCode}
        />
      )}
    </section>
  )
}



// Sub-components
function ActionButton({ icon, label }: { icon: string; label: string }) {
  const icons: Record<string, React.ReactNode> = {
    star: (
      <svg
        className="size-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    ),
    share: (
      <svg
        className="size-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
        />
      </svg>
    ),
    copy: (
      <svg
        className="size-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    ),
  }

  return (
    <button className="flex h-10 w-[98px] items-center justify-center gap-2 rounded-lg border-2 border-[#d4d4d4] bg-white text-[13px] font-semibold text-[#404040] transition-all hover:border-[#1E40AF] hover:bg-[#EFF6FF] hover:text-[#1E40AF]">
      {icons[icon]}
      {label}
    </button>
  )
}

function MetadataItem({
  icon,
  label,
  value,
}: {
  icon: string
  label: string
  value: string
}) {
  const icons: Record<string, React.ReactNode> = {
    eye: (
      <svg
        className="size-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    ),
    star: (
      <svg
        className="size-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    ),
    calendar: (
      <svg
        className="size-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
    sync: (
      <svg
        className="size-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
  }

  return (
    <div className="flex items-center gap-2 text-[13px] text-[#525252]">
      <span className="text-[#1E40AF]">{icons[icon]}</span>
      <span>
        <strong className="font-semibold text-[#171717]">{value}</strong>{' '}
        {label}
      </span>
    </div>
  )
}
